import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import modalFullScreenStore from 'shared/ui/ModalFullScreen/store/modalFullScreenStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type AccessRequest } from 'entities/Users/model/AccessRequest'
import { UsersApi } from 'entities/Users/api/users'
import { type EnumUserRoles } from 'entities/Users'
import { AssignMemberToInbox } from 'pages/settings/pages/members/ui/AssignMemberToInbox'
import { AssignMemberToInboxStore } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/assignMemberToInboxStore'
import { SalesmsgAccessFlow } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/flows'
import { RejectAccessRequestStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/RejectAccessRequestModal/store/rejectAccessRequestStore'
import { type SettingsStore } from 'pages/settings/store/settingsStore'

export class ReviewSalesmsgAccessStore {
  readonly modalId = nanoid()
  private _assignMemberToInboxStore: AssignMemberToInboxStore

  constructor(
    private _entities: IEntitiesStore,
    private _settings: SettingsStore,
    private _onSuccess?: () => void
  ) {
    const flow = new SalesmsgAccessFlow(
      () => this._assignMemberToInboxStore.accessRequest?.user.email,
      () => this._assignMemberToInboxStore.role,
      this.createInvite,
      this.handleSuccess
    )

    this._assignMemberToInboxStore = new AssignMemberToInboxStore({
      entities: this._entities,
      settings: this._settings,
      mode: 'salesmsgAccess',
      onCancel: this.onClose,
      onReject: this.openRejectModal,
      flow,
    })
    makeAutoObservable(this)
  }

  private createInvite = async (
    email: string,
    role: EnumUserRoles,
    inboxIds: number[]
  ): Promise<boolean> => {
    try {
      await UsersApi.createInvites({
        items: [{ email, role, team_ids: inboxIds }],
      })
      return true
    } catch (error) {
      logger.error(error)
      toastStore.add({ type: 'error', title: 'Failed to approve access' })
      return false
    }
  }

  openModal = (accessRequest: AccessRequest) => {
    this._assignMemberToInboxStore.setAccessRequestData(accessRequest)

    modalFullScreenStore.addModal({
      id: this.modalId,
      title: 'Salesmsg access',
      ModalContent: AssignMemberToInbox,
      ModalContentProps: {
        store: this._assignMemberToInboxStore,
      },
      onClose: this.onClose,
    })
  }

  openRejectModal = () => {
    const accessRequest = this._assignMemberToInboxStore.accessRequest
    if (!accessRequest) return

    new RejectAccessRequestStore(() => {
      this._onSuccess?.()
      this.onClose()
    }).openModal(accessRequest)
  }

  handleSuccess = () => {
    this._onSuccess?.()
    this.onClose()
  }

  onClose = () => {
    this._assignMemberToInboxStore.reset()
    modalFullScreenStore.removeModal(this.modalId)
  }
}
